"use client";

/**
 * Product detail modal for industry landing pages.
 * Mirrors the homepage Premium Services modal layout:
 * title + description + bullets + Pro Tip on the left, image on the right.
 * Quote CTA uses the same GlowEffectButton as the homepage service modal.
 * Keep overflow visible on the panel so the soft glow is not clipped into a blue bar.
 */
import Image from "next/image";
import Link from "next/link";
import dynamic from "next/dynamic";
import { useState } from "react";
import { AnimatePresence, m } from "framer-motion";
import type { IndustryProduct } from "@/data/industry-verticals";
import { trackIndustryCtaClick } from "@/lib/analytics";
import { GlowEffectButton } from "@/components/ui/glow-effect-button";

const LazyContactModal = dynamic(
  () => import("@/components/ui/contact-modal").then((mod) => mod.ContactModal),
  { ssr: false }
);

type IndustryProductDetailModalProps = {
  product: IndustryProduct;
  // Industry slug for analytics (e.g. "highschool").
  vertical: string;
  onClose: () => void;
};

export function IndustryProductDetailModal({
  product,
  vertical,
  onClose,
}: IndustryProductDetailModalProps) {
  const [isContactOpen, setIsContactOpen] = useState(false);

  const openContactFromModal = () => {
    trackIndustryCtaClick({
      vertical,
      ctaLocation: "product_modal",
      label: "Request Quote",
    });
    setIsContactOpen(true);
  };

  return (
    <>
      <AnimatePresence>
        <m.div
          className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          {/* Backdrop - solid dim only, no colored glow layers */}
          <m.div
            className="absolute inset-0 bg-black/70 backdrop-blur-md"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          {/* Modal panel - same spring entrance and overflow behavior as homepage */}
          <m.div
            key="industry-modal-content"
            className="relative mx-4 flex max-h-[80vh] w-full max-w-md flex-col rounded-2xl border border-white/10 bg-zinc-900 shadow-2xl md:max-w-4xl"
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
          >
            {/* Close button - same hover/tap motion as homepage */}
            <m.button
              type="button"
              className="absolute right-4 top-4 z-10 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border border-white/10 bg-white/10 backdrop-blur-sm"
              onClick={onClose}
              aria-label="Close product details"
              whileHover={{ scale: 1.1, backgroundColor: "rgba(255, 255, 255, 0.2)" }}
              whileTap={{ scale: 0.9 }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="white"
                className="h-5 w-5"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </m.button>

            <div className="flex-1 overflow-y-auto p-4 md:p-8">
              {/* Title slides up (homepage header motion) */}
              <m.div
                className="mb-6 md:mb-8"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
              >
                <h2 className="text-2xl font-bold text-white md:text-3xl">
                  {product.name}
                </h2>
              </m.div>

              <div className="flex flex-col gap-8 md:flex-row">
                {/* Left copy column slides in from the left */}
                <m.div
                  className="w-full md:w-1/2"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <h3 className="mb-4 text-xl font-semibold text-white">
                    {product.subtitle}
                  </h3>
                  <p className="mb-6 leading-relaxed text-zinc-300">
                    {product.description}
                  </p>

                  <ul className="mb-6 list-disc space-y-1 pl-5 text-base font-medium text-[#8AD4E1]">
                    {product.details.map((detail) => (
                      <li key={detail}>{detail}</li>
                    ))}
                  </ul>

                  {/* Homepage-style Pro Tip linking to a related blog post */}
                  <p className="mb-6 text-sm leading-relaxed text-zinc-400">
                    <span className="font-semibold text-[#8AD4E1]">Pro Tip:</span>{" "}
                    <Link
                      href={`/blog/${product.proTip.slug}`}
                      className="text-[#8AD4E1] underline decoration-[#8AD4E1]/40 underline-offset-2 transition-colors hover:text-[#7BC0CD] hover:decoration-[#7BC0CD]"
                    >
                      Read our guide on {product.proTip.title}
                    </Link>
                  </p>

                  {/* Quote + catalog CTAs - same side-by-side layout as homepage modal */}
                  <div className="flex flex-wrap gap-4">
                    <GlowEffectButton
                      onClick={openContactFromModal}
                      colors={["#8AD4E1", "#8AD4E1"]}
                      mode="static"
                      className="text-base py-2.5 text-[#8AD4E1]"
                    >
                      Request Quote
                    </GlowEffectButton>

                    {/* Deep-links to the matching /products category section */}
                    <Link
                      href={product.productsHref}
                      onClick={() => {
                        trackIndustryCtaClick({
                          vertical,
                          ctaLocation: "product_modal",
                          label: product.productsCtaLabel,
                        });
                      }}
                      className="inline-flex items-center gap-1 rounded-md border border-white/10 bg-transparent px-3 py-2.5 text-base text-white transition-colors hover:bg-white/10"
                    >
                      {product.productsCtaLabel}
                      <span aria-hidden="true">→</span>
                    </Link>
                  </div>
                </m.div>

                {/* Right image slides in from the right */}
                <m.div
                  className="min-h-[300px] w-full overflow-hidden rounded-xl border border-white/10 bg-zinc-800 md:w-1/2"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <div className="relative h-[300px] w-full md:h-full">
                    <Image
                      src={product.imageSrc}
                      alt={product.imageAlt}
                      fill
                      sizes="(max-width: 768px) 100vw, 50vw"
                      className="object-cover"
                      priority
                    />
                  </div>
                </m.div>
              </div>
            </div>

            {/* Footer slides up from the bottom */}
            <m.div
              className="border-t border-white/5 bg-zinc-800/50 p-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <p className="text-sm text-zinc-400">Available for custom orders</p>
            </m.div>
          </m.div>
        </m.div>
      </AnimatePresence>

      {isContactOpen && (
        <LazyContactModal
          isOpen={isContactOpen}
          onClose={() => setIsContactOpen(false)}
          analyticsSource="industry_product_modal"
          analyticsVertical={vertical}
        />
      )}
    </>
  );
}
